import React, { useContext, useState, useRef, useEffect, useCallback, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

// d.ts
import { IDataController, DCRecord, DCInsertResponseSuccess } from "@/@types/lib/dataController"

// MUI Icons
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// MUI
import Tooltip from "@mui/material/Tooltip";

// Custom Components
import ModelTable from "@/ui/Table/ModelTable";
import DialogContext from "@/ui/DialogContext/DialogContext";

import modelKatovi from "@/models/katovi"
import katoviService from "@/services/katoviService";
import dataController from "@/lib/dataController";
import { sifrarnici as S } from "@/lib/sifrarnici";
import LoaderContext from "@/components/LoaderContext/LoaderContext";
import OperationsContext from "@/components/OperationsContext/OperationsContext";
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";
import ObjectAnalysisKatDialog from "@/views/Operations/ObjectAnalysisKatDialog";
import TableHeaderButton from "@/ui/Table/HeaderButtons/TableHeaderButton";

//Types
import { LoaderContextType } from "@/@types/context/LoaderContext";
import { SnackbarContextType } from "@/@types/ui/SnackbarContext";
import { OperationsContextType } from "@/components/OperationsContext/OperationsContext";
import { DialogContextType } from "@/@types/ui/DialogContext";
import { IResolve } from "@/lib/ActPromise";

// TODO:
// subModels cleanup: they are added to the project because Table.jsx can not function without them

interface IObjectAnalysisKatoviTableProps {
  isDisabled: boolean;
  baseRecordPath: string;
  baseRecordId: number | null;

  dijelovi: DCRecord[]

  selectedId: number;
  onRowSelect: (id: number, record: DCRecord | null) => any;
  recalculateKat: Array<DCRecord>;
  onDataChanged: (records: DCRecord[]) => void;
  checkIfDioChanged: () => Promise<boolean> | IResolve<boolean>;
  saveEditingDio: () => Promise<any>;
}

const ObjectAnalysisKatoviTable: FunctionComponent<React.PropsWithChildren<IObjectAnalysisKatoviTableProps>> = (props) => {
  const dialogContext = useContext<DialogContextType>(DialogContext);
  const loaderContext = useContext(LoaderContext) as LoaderContextType;
  const snackbarContext = useContext(SnackbarContext) as SnackbarContextType;
  const operationsContext = useContext<OperationsContextType>(OperationsContext);
  const [defaultSelectedRow, setDefaultSelectedRow] = useState<number>(-1);

  const [records, setRecords] = useState<DCRecord[]>([]);
  const [revisionCounter, setRevisionCounter] = useState(0);
  const [idToDelete, setIdToDelete] = useState(-1);
  const [events, setEvents] = useState<boolean>(false);
  const [pending, setPending] = useState<boolean>(false);

  const recordsRef = useRef<DCRecord[]>([]);

  const { t } = useTranslation();

  const { baseRecordPath, baseRecordId, selectedId, onRowSelect, onDataChanged, recalculateKat, isDisabled, dijelovi } = props;
  const { checkIfDioChanged, saveEditingDio } = props;

  const endsWithSlash = baseRecordPath.lastIndexOf('/') === baseRecordPath.length - 1 ? true : false;
  const url = `${baseRecordPath}${endsWithSlash ? "" : "/"}${baseRecordId}/katovi`;

  const dc: IDataController = new dataController(modelKatovi, url);

  useEffect(() => {
    if (baseRecordId) {
      if (events) {
        unregisterEvents();
      }
      registerEvents();
      setEvents(true);
      return () => unregisterEvents()
    }
    return () => { };
  }, [baseRecordId, isDisabled, records, pending])

  const eventRegistrationFn = (evt: KeyboardEvent) => {
    if (evt.altKey) {
      switch (evt.code) {
        case 'KeyA': if (!isDisabled) { handleAdd(-1) } break;
        case 'KeyS': if (!isDisabled) { handleAdd(1) } break;
        case 'KeyV': if (!isDisabled) { handleAddCopy(1) } break;
      }
    }
  }

  const registerEvents = () => {
    window.addEventListener('keydown', eventRegistrationFn);
  }

  const unregisterEvents = () => {
    window.removeEventListener('keydown', eventRegistrationFn)
  };

  useEffect(() => {
    if (baseRecordId !== null) {
      refreshRecords();
    } else {
      setRecords([]);
    }
  }, [baseRecordId])

  useEffect(() => {
    refreshRecords();
  }, [revisionCounter])

  useEffect(() => {
    recordsRef.current = records;
    if (onDataChanged) {
      onDataChanged(records);
    }
    // let recMinTavan = -1;
    // records.forEach(r => {
    //   const value = r.kat_tip_id && typeof r.kat_tip_id === 'object' && 'value' in r.kat_tip_id
    //     ? r.kat_tip_id.value
    //     : r.kat_tip_id
    //   if (value === S.kat_tip_id['T']) {
    //     recMinTavan = parseInt(r.broj_kata as string, 10)
    //   }
    // });
    // // setMinTavan(recMinTavan);
    // setDisplayRecords(records.map(r => ({
    //   ...r,
    //   allowPodrumAndSuteren: parseInt(r.broj_kata as string, 10) <= recMinTavan,
    // })))
  }, [records]);

  useEffect(() => {
    if (recalculateKat.length > 0) {
      const firstDio = recalculateKat[0];
      const katId = firstDio.kat_id;

      const broj_dijelova = recalculateKat.length;
      const sumPrePb = recalculateKat.map(x => parseFloat(x.pre_pb as string) || 0).reduce((a, b) => a + b, 0);
      const details_namjena_pb = recalculateKat.map(x => { return { namjena_id: x.namjena_id, pre_pb: x.pre_pb } })
      const details_namjena_pn = recalculateKat.map(x => { return { namjena_id: x.namjena_id, pre_pn: x.pre_pn } })
      setRecords(recs => recs.map(r => r.id !== katId ? r : { ...r, broj_dijelova: broj_dijelova, sum_pre_pb: sumPrePb, details_namjena_pb: details_namjena_pb, details_namjena_pn: details_namjena_pn }));
    } else {
      setRecords(recs => recs.map(r => r.id !== selectedId ? r : { ...r, broj_dijelova: 0, sum_pre_pb: 0 }));
    }

  }, [recalculateKat])

  const refreshRecords = () => {
    // loaderContext.toggleLoading(true);
    if (baseRecordId === null) return;
    dc.GetData()
      .then((resp) => {
        if (resp.success && Array.isArray(resp.data)) {
          setRecords(resp.data);
          if (resp.data.length === 0) {
            snackbarContext.showNotification("messages.nodata_katovi", "warning");
          }
        };
      })
      .catch((resp) => {
        console.log(resp);
      })
      .finally(() => {
        // loaderContext.toggleLoading(false);
      });
  };

  const handleRowSelect = (id: number) => {
    let onlyChangeValue = false;
    if (id) {
      //@ts-ignore
      if (!isDisabled && window.__GlobalState['button-group-ctrl']) {
        onlyChangeValue = true;
        //@ts-ignore
        const katTIdValue = window.__GlobalState['button-group-ctrl'].value
        const nRecord = records.find(r => r.id === id);
        const currKatTIdValue = nRecord && nRecord.kat_tip_id
          ? typeof nRecord.kat_tip_id === 'object' && 'value' in nRecord.kat_tip_id
            ? nRecord.kat_tip_id.value
            : nRecord.kat_tip_id
          : null;
        const saveRecs = records.map(r => ({ ...r }));
        const nKatTIdValue = katTIdValue === currKatTIdValue ? null : katTIdValue;
        setRecords((recs: DCRecord[]) =>
          recs.map((r: DCRecord) => r.id === id
            ? { ...r, kat_tip_id: nKatTIdValue }
            : { ...r })
        );
        katoviService.partiallyUpdateKat(baseRecordId as number, id as number, { kat_tip_id: nKatTIdValue })
          .then(resp => {
            console.log("GREAT SUCCESS!!");
          })
          .catch(err => {
            console.log(err);
            setRecords([...saveRecs]);
          });
        //@ts-ignore
        window.__GlobalState['button-group-ctrl'] = null;
      }
    }
    if (selectedId === id || onlyChangeValue) return false;
    const katSelected = records.find(kat => kat.id === id);
    onRowSelect(id, katSelected || null);
    return true;
  }

  const handleDialogClose = (result: { dataChanged: boolean; action: string }) => {
    if (result.dataChanged) {
      refreshRecords();
    }
  };

  const handleAdd = (direction: 1 | -1) => {
    const { selectedProject } = operationsContext;
    if (pending || selectedProject === null || baseRecordId === null) return;
    setPending(true);
    const { id: projektId } = selectedProject;
    let min: number = records.length === 0 ? 0 : Infinity;
    let max: number = records.length === 0 ? 0 : -Infinity;
    (records || []).forEach(kat => {
      max = Math.max((kat.broj_kata as number) || 0, max);
      min = Math.min((kat.broj_kata as number) || 0, min);
    });
    const newFloor: DCRecord = {
      broj_kata: records.length === 0 ? 0 : (direction === 1 ? max + 1 : min - 1),
      projekt_id: projektId,
    }
    if (min === 0 && max === 0) {
      console.error("An issue occurred with katovi. No kat found, must be at least one!");
    }

    // loaderContext.toggleLoading(true);
    console.time("ADD KAT");
    dc.InsertRecord(newFloor)
      .then((resp) => {
        const { id } = resp as DCInsertResponseSuccess;
        newFloor.id = id;
        newFloor.objekti_id = baseRecordId;
        newFloor.kat_tip_id = null;
        newFloor.pre_pb = 0;
        setRecords(recs =>
          direction === 1
            ? [newFloor, ...recs]
            : [...recs, newFloor]
        );
        onRowSelect(id, { ...newFloor });
        console.timeEnd("ADD KAT");
      })
      .catch(err => console.log(err))
      .finally(() => setPending(false));
    // .finally(() => loaderContext.toggleLoading(false));
  }

  const handleAddCopy = (direction: 1 | -1) => {
    const { selectedProject } = operationsContext;
    if (pending || selectedProject === null || baseRecordId === null) return;
    setPending(true);
    const { id: projektId } = selectedProject;
    let min: number = records.length === 0 ? 0 : Infinity;
    let max: number = records.length === 0 ? 0 : -Infinity;
    (records || []).forEach(kat => {
      max = Math.max((kat.broj_kata as number) || 0, max);
      min = Math.min((kat.broj_kata as number) || 0, min);
    });
    const selectedFloor = records.find(record => record?.id == selectedId);

    const newFloor: DCRecord = {
      ...selectedFloor,
      broj_kata: records.length === 0 ? 0 : (direction === 1 ? max + 1 : min - 1),
      projekt_id: projektId,
      dijelovi: dijelovi
    }
    if (min === 0 && max === 0) {
      console.error("An issue occurred with katovi. No kat found, must be at least one!");
    }

    // loaderContext.toggleLoading(true);
    console.time("ADD KAT COPY");
    dc.InsertRecord(newFloor, url + "/" + selectedId + "/copy")
      .then((resp) => {
        const { id } = resp as DCInsertResponseSuccess;
        newFloor.id = id;
        newFloor.objekti_id = baseRecordId;
        setRecords(recs =>
          direction === 1
            ? [newFloor, ...recs]
            : [...recs, newFloor]
        );
        onRowSelect(id, { ...newFloor });
        console.timeEnd("ADD KAT COPY");
      })
      .catch(err => console.log(err))
      .finally(() => setPending(false));
    // .finally(() => loaderContext.toggleLoading(false));
  }

  const handleEdit = (record: DCRecord) => {
    if (record === null) return;
    const { id, objekti_id, kat_tip_id } = record;
    const katTIdValue = kat_tip_id
      && typeof kat_tip_id === 'object'
      && 'value' in kat_tip_id
      ? kat_tip_id.value
      : kat_tip_id;
    katoviService.partiallyUpdateKat(objekti_id as number, id as number, { kat_tip_id: katTIdValue })
      .then(resp => {
        if (resp.success) {
          setRecords((recs: DCRecord[]) =>
            recs.map((r: DCRecord) => r.id === id ? { ...record } : { ...r })
          );
        }
        return Promise.reject(resp);
      })
      .catch(err => console.log(err))
  }

  const allowDelete = useCallback((action: string, recId: number) => {
    if (recordsRef.current.length < 2) {
      return false;
    }
    const rec = recordsRef.current.find((x: DCRecord) => x.id as number === recId);
    if (rec) {
      const ind = recordsRef.current.indexOf(rec);
      if (ind === 0 || ind === recordsRef.current.length - 1) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }, [records, records.length]);

  const handleDeleted = (id?: number) => {
    if (!id) return;
    setIdToDelete(id);
  };

  useEffect(() => {
    if (idToDelete !== -1) {
      checkIfDioChanged().then(canContinue => {
        if (!canContinue) {
          saveEditingDio().catch((error) => {
            snackbarContext.showNotification(`Pogreška kod spremanja dijela! ${JSON.stringify(error)}`, "error", true);
          });
        }
        const url = `${baseRecordPath}${endsWithSlash ? "" : "/"}${baseRecordId}/katovi`;
        dc.DeleteRecord(idToDelete as number, url).then((resp) => {
          if (resp.success) {
            setRevisionCounter(cnt => cnt + 1);
            onRowSelect(-1, null);
          } else {
            if (snackbarContext !== null) {
              //@ts-ignore
              snackbarContext.showNotification(resp.error, "error");
            }
          }
        })
          .catch((resp) => {
            if (snackbarContext !== null) {
              //@ts-ignore
              snackbarContext.showNotification(resp.error.message, "error");
            }
          })
      });
      setIdToDelete(-1);
    }
  }, [idToDelete])

  useEffect(() => {
    if (records && records !== null && Array.isArray(records) && records.length > 0 && selectedId === -1) {
      const defaultRow = records.find((x) => x.broj_kata === 0)
      if (defaultRow && defaultRow !== null && defaultRow.id && !isDisabled) {
        setDefaultSelectedRow(Number(defaultRow?.id));
        handleRowSelect(Number(defaultRow?.id));
      }
    }
  }, [records]);

  return (
    <ModelTable
      title={t("titles.katovi")}
      records={records}
      viewName="default"
      dc={dc}
      allowSearchBar={false}
      allowSelection="one"
      allowFilter={false}
      allowExport={false}
      allowColumnPicker={false}
      allowEdit={!isDisabled}
      allowInlineEdit={false}
      allowInlineEditQuickly={false}
      allowDelete={!isDisabled && allowDelete}
      useDeleteButton={false}
      onDeleted={handleDeleted}
      allowInc={false}
      disableControls={!isDisabled}
      disableInlineControls={isDisabled}
      preventEventDefault={false}
      showControlSwitch={false}
      onRowAction={(id: any) => { console.log(id) }}
      onRowClick={handleRowSelect}
      rowSelectedCustom={selectedId !== -1 ? selectedId : defaultSelectedRow}
      initialPageSize={25}
      allowRowAction={() => { return true; }}
      trackTableState={false}
      unselectRow={false}
      customRecordCount={records.length}
      gotoFirstPageOnSelect={true}
      onRecordChange={handleEdit}
      CustomAddComponent={(
        <>
          <Tooltip title={baseRecordId === null || isDisabled ? "" : t("tooltips.shortcuts.copy_kat+1") as string}>
            <TableHeaderButton
              startIcon={<KeyboardArrowUpIcon />}
              onClick={() => handleAddCopy(1)}
              variant="contained"
              disabled={baseRecordId === null || isDisabled || selectedId == -1}
            >
              {t("buttons.add_duplicate_top")}
            </TableHeaderButton>
          </Tooltip>
          {
            // <Tooltip title={baseRecordId === null || isDisabled ? "" : t("tooltips.shortcuts.add_kat-1") as string}>
            //   <TableHeaderButton
            //     startIcon={<KeyboardArrowDownIcon />}
            //     onClick={() => handleAddCopy(-1)}
            //     variant="contained"
            //     disabled={baseRecordId === null || isDisabled || selectedId == -1}
            //   >
            //     {t("buttons.add_duplicate_bottom")}
            //   </TableHeaderButton>
            // </Tooltip>
          }
          <Tooltip title={baseRecordId === null || isDisabled ? "" : t("tooltips.shortcuts.add_kat+1") as string}>
            <TableHeaderButton
              startIcon={<KeyboardArrowUpIcon />}
              onClick={() => handleAdd(1)}
              variant="contained"
              disabled={baseRecordId === null || isDisabled}
            >
              {t("buttons.add_above")}
            </TableHeaderButton>
          </Tooltip>
          <Tooltip title={baseRecordId === null || isDisabled ? "" : t("tooltips.shortcuts.add_kat-1") as string}>
            <TableHeaderButton
              startIcon={<KeyboardArrowDownIcon />}
              onClick={() => handleAdd(-1)}
              variant="contained"
              disabled={baseRecordId === null || isDisabled}
            >
              {t("buttons.add_below")}
            </TableHeaderButton>
          </Tooltip>
        </>
      )}
    />
  );
}

export default ObjectAnalysisKatoviTable;
